import React, { useState, useEffect } from 'react';
import "./Card.css";
import { useNavigate, useLocation } from 'react-router-dom';
import cardd from "./card.png";
import Modal from 'react-modal';
import { useImage } from "../../context/ImageContext.js";
import Loader from '../../components/Loader.js';
import { GoTrash } from "react-icons/go";
import { FaShare } from "react-icons/fa";
import { useToast } from '../../context/ToastContext.js';

// RTK Query hooks
import {
  useGetCartQuery,
  useDeleteFromCartMutation,
  useAddServiceMutation,
  useShareCartMutation,
} from '../../services/cartApi';

// Komponentleri import ediyoruz
import EmptyCart from '../../components/card/EmptyCart';
import CartItem from '../../components/card/CartItem';
import OrderSummary from '../../components/card/OrderSummary';
import DeliveryForm from '../../components/card/DeliveryForm';
import ShareModal from '../../components/card/ShareModal';

Modal.setAppElement('#root');

const Card = () => {
  const { getImageUrl } = useImage();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  // RTK Query hooks
  const { data: cart = [], isLoading: cartLoading, error: cartError } = useGetCartQuery();
  const [deleteFromCart] = useDeleteFromCartMutation();
  const [shareCart, { isLoading: isSharing }] = useShareCartMutation();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [referansKodu, setReferansKodu] = useState('');
  const [orderDiscount, setOrderDiscount] = useState(0);

  // Form validasyonu ve sözleşme state'leri DeliveryForm'a taşındı
  const [shareModalIsOpen, setShareModalIsOpen] = useState(false);
  const [generatedShareCode, setGeneratedShareCode] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [userAddresses, setUserAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);

  // Hizmet modalı için state'ler
  const [serviceModalIsOpen, setServiceModalIsOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [selectedServices, setSelectedServices] = useState({});

  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [kaporaInfo, setKaporaInfo] = useState(null);

  const total = cart.reduce((acc, product) => {
    // Backend'den gelen toplam_fiyat kullan (yüzük fiyatı dahil)
    return acc + parseFloat(product.toplam_fiyat || product.fiyat);
  }, 0);

  // Kapora kontrolü - ref kodu ile kapora engeli kaldırılmış ürünleri kontrol et
  const hasKaporaInCart = () => {
    // Ref kodu yoksa veya kapora_info yoksa normal kapora kontrolü yap
    if (!kaporaInfo || !kaporaInfo.success) {
      return cart.some((product) => Number(product.kapora) === 1);
    }

    // ÖNEMLİ: Önce kapsanmayan kaporalı ürünleri kontrol et
    // Eğer ref kodu ile kapsanmayan kaporalı ürünler varsa, hala kapora engeli var
    if (kaporaInfo.kapora_products && kaporaInfo.kapora_products.length > 0) {
      return true;
    }

    // Eğer allowed_products varsa ve kapora_products yoksa, tüm kaporalı ürünler kapsanmış
    if (kaporaInfo.allowed_products && kaporaInfo.allowed_products.length > 0) {
      return false;
    }

    // Diğer durumlarda normal kapora kontrolü yap
    return cart.some((product) => Number(product.kapora) === 1);
  };

  const shippingTotal = cart.reduce((acc, product) => {
    return acc + parseFloat(product.kargo_fiyati);
  }, 0);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const tkn = localStorage.getItem('tkn');
    setIsAuthenticated(!!token && !!tkn);

    const savedAddresses = localStorage.getItem('userAddresses');
    if (savedAddresses) {
      const addresses = JSON.parse(savedAddresses);
      setUserAddresses(addresses);
      if (addresses.length > 0) {
        setSelectedAddressId(addresses[0].id);
      }
    }
  }, []);

  useEffect(() => {
    if (location.state?.directCheckout && cart.length > 0) {
    }
  }, [cart, location.state]);

  useEffect(() => {
    // Cookie'den referans kodunu al
    const getCookieValue = (name) => {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.startsWith(name + '=')) {
          return cookie.substring(name.length + 1);
        }
      }
      return '';
    };

    const shareRef = getCookieValue('share_ref');
    if (shareRef) {
      setReferansKodu(shareRef);
    }
  }, []);

  const handleFormSubmitSuccess = (customerFormData) => {
    try {
      // Toplam tutarı hesapla (ürün toplamı + kargo ücreti - indirim)
      const finalTotal = parseFloat(total) + parseFloat(shippingTotal) - orderDiscount;

      const paymentRoute = customerFormData.paymentType === 'eft' ? '/eft-payment' : '/payment';

      navigate(paymentRoute, {
        state: {
          total: finalTotal,
          customerForm: customerFormData,
          cart: cart,
          referansKodu,
          appliedDiscount: orderDiscount
        }
      });
    } catch (error) {
      console.error('Hata:', error);
      showToast('İşlem sırasında bir hata oluştu', 'error');
    }
  };

  const handleShareCart = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      // RTK Query ile sepet paylaşma
      const result = await shareCart().unwrap();
      if (result && result.shareCode) {
        const url = `${window.location.origin}/shared-cart/${result.shareCode}`;
        setGeneratedShareCode(result.shareCode);
        setShareUrl(url);
        setShareModalIsOpen(true);
      } else {
        showToast('Sepet paylaşma işlemi sırasında bir hata oluştu', 'error');
      }
    } catch (error) {
      console.error('Sepet paylaşma hatası:', error);
      showToast('Sepet paylaşılırken bir hata oluştu', 'error');
    }
  };

  // RTK Query ile sepetten ürün silme
  const handleDeleteCard = async (id) => {
    try {
      await deleteFromCart(id);
      showToast('Ürün sepetten kaldırıldı', 'success');
    } catch (error) {
      console.error('Ürün silinirken hata:', error);
      showToast('Ürün silinirken bir hata oluştu', 'error');
    }
  };

  if (cartError) {
    showToast('Sepet yüklenirken bir hata oluştu', 'error');
  }

  if (cart.length === 0 && !cartLoading) {
    return <EmptyCart />;
  }

  if (cartLoading) return <Loader />;

  return (
    <div className="checkout-page">
      <div className='card-product'>
        <div className='card-title'>
          <img src={cardd} alt="card" />
          <span>Sepetim</span>
          <button
            className="btn btn-bikuyum btn-sm ms-auto"
            onClick={handleShareCart}
            title="Sepeti Paylaş"
            disabled={isSharing}
          >
            <FaShare /> {isSharing ? 'Paylaşılıyor...' : 'Sepeti Paylaş'}
          </button>
        </div>

        <div className="product-listt">
          {cart.map((product, index) => (
            <CartItem
              key={index}
              product={product}
              deleteCard={handleDeleteCard}
              getImageUrl={getImageUrl}
              kaporaInfo={kaporaInfo}
            />
          ))}
        </div>
      </div>

      <div className="order-summary">
        <OrderSummary
          total={total}
          shippingTotal={shippingTotal}
          referenceCode={referansKodu}
          onRefCodeChange={(newRefCode) => setReferansKodu(newRefCode)}
          cartItems={cart}
          onDiscountUpdate={setOrderDiscount}
          onKaporaInfoChange={setKaporaInfo}
        />

        <DeliveryForm
          // formData={formData} // Artık kendi state'i var
          // setFormData={setFormData} // Artık kendi state'i var
          referansKodu={referansKodu}
          onSubmitSuccess={handleFormSubmitSuccess}
          isDepositInCart={hasKaporaInCart()}
        />
      </div>

      <ShareModal
        isOpen={shareModalIsOpen}
        onClose={() => setShareModalIsOpen(false)}
        shareUrl={shareUrl}
        generatedShareCode={generatedShareCode}
      />
    </div>
  );
};

export default Card;